"use client";

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { extractPreparedSettingsPatchExecution } from "@/components/ai-chat/agent-execution-adapter";
import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";
import type { ChatHistoryEntry } from "@/types/chat-thread";
import { refreshSettingsOutcome } from "./refresh-settings-outcome";
import { extractSettingsApplyOutcome } from "./settings-native-approval";
import { useAskDecodaStore } from "@/stores/ask-decoda-store";

/** Also refresh results received while the patch page/card is unmounted. */
export function useSettingsResultObserver() {
  const $ = _c(12);
  const client = useQueryClient();
  const {
    tenant,
    refetchTenant
  } = useTenant();
  const {
    refetchUser
  } = useUser();
  const epoch = useAskDecodaStore(_temp);
  let t0;
  if ($[0] !== client || $[1] !== epoch || $[2] !== refetchTenant || $[3] !== refetchUser || $[4] !== tenant?.subDomain) {
    t0 = () => {
      const seen = new Set();
      const scan = () => {
        if (useAskDecodaStore.getState().runtimeEpoch !== epoch) {
          return;
        }
        for (const query of client.getQueryCache().getAll()) {
          if (!query.queryKey.includes(tenant?.subDomain) || !Array.isArray(query.state.data)) {
            continue;
          }
          const history = query.state.data as ChatHistoryEntry[];
          if (!history.every(_temp2)) {
            continue;
          }
          const executions = history.flatMap(_temp3);
          const patches = executions.map(extractPreparedSettingsPatchExecution).filter(_temp4);
          for (const execution of executions) {
            for (const patch_0 of patches) {
              if (!patch_0) {
                continue;
              }
              const outcome = extractSettingsApplyOutcome(execution, patch_0.patchId);
              const key = `${execution.id}:${execution.attempt}`;
              if (!outcome || seen.has(key) || outcome.kind === "failed") {
                continue;
              }
              seen.add(key);
              refreshSettingsOutcome({
                client,
                tenant: tenant?.subDomain,
                epoch,
                executionId: key,
                patch: patch_0,
                outcome,
                refetchUser,
                refetchTenant
              });
            }
          }
        }
      };
      scan();
      return client.getQueryCache().subscribe(scan);
    };
    $[0] = client;
    $[1] = epoch;
    $[2] = refetchTenant;
    $[3] = refetchUser;
    $[4] = tenant?.subDomain;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  const t1 = tenant?.subDomain;
  let t2;
  if ($[6] !== client || $[7] !== epoch || $[8] !== refetchTenant || $[9] !== refetchUser || $[10] !== t1) {
    t2 = [client, t1, epoch, refetchUser, refetchTenant];
    $[6] = client;
    $[7] = epoch;
    $[8] = refetchTenant;
    $[9] = refetchUser;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  useEffect(t0, t2);
}
function _temp4(patch) {
  return Boolean(patch);
}
function _temp3(entry_0) {
  return entry_0.executions ?? [];
}
function _temp2(entry) {
  return entry && typeof entry === "object" && "executions" in entry;
}
function _temp(state) {
  return state.runtimeEpoch;
}